import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import IcfLayout, { ScreenHeader, StepCta } from "./IcfLayout";
import StoryNarratorBar from "./story/StoryNarratorBar";
import {
    ChevronDown,
    ChevronRight,
    FileText,
    BookOpen,
    Loader2,
    Play,
    Plus,
    RefreshCw,
} from "lucide-react";
import { SOURCES, RULES, RULE_DIFF } from "../../data/icfSeed";

const iconMap = { FileText, BookOpen };

const SourceCard = ({ src }) => {
    const Icon = iconMap[src.icon] || FileText;
    return (
        <div className="border border-hair bg-white p-4 flex items-start gap-3">
            <div className="w-9 h-9 border border-hair rounded-sm flex items-center justify-center shrink-0">
                <Icon size={16} strokeWidth={1.5} />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[13px] heading font-medium tracking-tight leading-tight">
                    {src.title}
                </div>
                <div className="text-[11px] text-neutral-500 mono mt-1 truncate">
                    {src.subtitle}
                </div>
            </div>
            <span className="mono text-[10px] uppercase tracking-wider px-2 py-[2px] border rounded-sm shrink-0 text-[#166534] bg-[#DCFCE7] border-[#86EFAC]">
                {src.status}
            </span>
        </div>
    );
};

const TYPE_PILL = {
    Required: "text-[#991B1B] bg-[#FEE2E2] border-[#FCA5A5]",
    Conditional: "text-[#9A3412] bg-[#FFEDD5] border-[#FED7AA]",
    "Sponsor-specific": "text-[#1E40AF] bg-[#DBEAFE] border-[#BFDBFE]",
    "Protocol-derived": "text-[#5B21B6] bg-[#EDE9FE] border-[#DDD6FE]",
};

const BADGE_PILL = {
    NEW: "text-[#166534] bg-[#DCFCE7] border-[#86EFAC]",
    MODIFIED: "text-[#9A3412] bg-[#FFEDD5] border-[#FED7AA]",
};

const RuleCard = ({ rule, defaultOpen }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="border border-hair bg-white" data-testid={`rule-card-${rule.id}`}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-neutral-50 transition-colors"
            >
                <div className="mt-1 shrink-0">
                    {open ? (
                        <ChevronDown size={14} className="text-neutral-500" />
                    ) : (
                        <ChevronRight size={14} className="text-neutral-500" />
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="mono text-[10px] text-[#C2410C]">
                            {rule.id}
                        </span>
                        <span className="mono text-[10px] text-neutral-500">
                            {rule.citation}
                        </span>
                        <span
                            className={`mono text-[10px] uppercase tracking-wider px-1.5 py-[1px] border rounded-sm ${
                                TYPE_PILL[rule.type] || "border-hair text-neutral-500"
                            }`}
                        >
                            {rule.type}
                        </span>
                        {rule.badge && (
                            <span
                                className={`mono text-[10px] uppercase tracking-wider px-1.5 py-[1px] border rounded-sm ${BADGE_PILL[rule.badge]}`}
                            >
                                {rule.badge}
                            </span>
                        )}
                    </div>
                    <div className="text-[13px] heading font-medium tracking-tight mt-2">
                        {rule.name}
                    </div>
                    {open && (
                        <div className="mt-3">
                            <p className="text-[12.5px] text-neutral-700 leading-relaxed">
                                {rule.description}
                            </p>
                            {rule.trigger && (
                                <div className="mt-3 text-[11.5px] italic text-[#9A3412] border-l-2 border-[#FED7AA] pl-3">
                                    Trigger: {rule.trigger}
                                </div>
                            )}
                            {rule.exact && (
                                <div className="mt-3 border-t border-hair pt-3">
                                    <div className="overline text-[9px] mb-2">
                                        See exact regulatory language
                                    </div>
                                    <p className="text-[12px] italic text-neutral-600 leading-relaxed">
                                        “{rule.exact}”
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </button>
        </div>
    );
};

const DiffCard = ({ entry }) => (
    <div
        className="border border-hair bg-white px-3 py-2.5 flex items-center gap-3"
        data-testid={`diff-${entry.id}`}
    >
        <span
            className={`mono text-[10px] uppercase tracking-wider px-1.5 py-[1px] border rounded-sm shrink-0 ${BADGE_PILL[entry.state]}`}
        >
            {entry.state}
        </span>
        <div className="min-w-0 flex-1">
            <div className="mono text-[10px] text-[#C2410C] truncate">
                {entry.id}
            </div>
            <div className="text-[12px] heading font-medium tracking-tight truncate">
                {entry.title}
            </div>
        </div>
    </div>
);

export default function IcfExtract() {
    const [searchParams] = useSearchParams();
    const storyMode = searchParams.get("story") === "1";
    // sessionStorage remembers that extraction has been run — so returning
    // from the story-mode punchline to free-explore mode doesn't reset the
    // view back to the "click Run Extraction" empty state.
    const persisted =
        typeof window !== "undefined" &&
        window.sessionStorage.getItem("icf_extracted") === "1";
    const [extracted, setExtracted] = useState(persisted);
    const [loading, setLoading] = useState(false);

    const runExtraction = () => {
        setLoading(true);
        setTimeout(() => {
            setLoading(false);
            setExtracted(true);
            try {
                window.sessionStorage.setItem("icf_extracted", "1");
            } catch (_e) {
                /* private mode */
            }
        }, 2000);
    };

    // Story mode auto-triggers extraction on land (respects 1a: auto-run).
    useEffect(() => {
        if (storyMode && !extracted && !loading) {
            runExtraction();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storyMode]);

    return (
        <IcfLayout>
            <ScreenHeader
                title="Extract — Rules from Protocol + Regulation"
                subtitle="AudRI extracts structured compliance rules from the protocol amendment, the 21 CFR 50.25 ruleset, and ICH-GCP E6(R3), producing a unified rule set the engine can execute against."
            />

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)_minmax(0,320px)] gap-6">
                {/* Left — Sources */}
                <aside data-testid="extract-sources">
                    <div className="overline mb-3">Sources</div>
                    <div className="flex flex-col gap-3">
                        {SOURCES.map((s) => (
                            <SourceCard key={s.id} src={s} />
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={runExtraction}
                        disabled={loading || extracted}
                        data-testid="btn-run-extraction"
                        className="w-full mt-5 inline-flex items-center justify-center gap-2 bg-[#0A0A0A] text-white text-sm px-4 py-3 rounded-sm hover:bg-[#262626] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <>
                                <Loader2 size={14} className="animate-spin" />
                                Extracting compliance rules…
                            </>
                        ) : extracted ? (
                            <>
                                <RefreshCw size={14} /> Extraction complete
                            </>
                        ) : (
                            <>
                                <Play size={14} /> Run Extraction
                            </>
                        )}
                    </button>
                    {loading && (
                        <div className="mt-3 h-[3px] bg-neutral-100 rounded-sm overflow-hidden">
                            <div
                                className="h-full bg-[#C2410C] rounded-sm"
                                style={{
                                    animation: "icfProgress 2s linear forwards",
                                }}
                            />
                        </div>
                    )}
                    <style>{`
                        @keyframes icfProgress {
                            from { width: 0%; }
                            to { width: 100%; }
                        }
                    `}</style>
                </aside>

                {/* Middle — Extracted rules */}
                <div className="min-w-0" data-testid="extract-rules">
                    <div className="overline mb-3 flex items-center justify-between">
                        <span>Extracted rules</span>
                        {extracted && (
                            <span className="mono text-[10px] text-neutral-500">
                                {RULES.length} rules · showing a curated sample of 24 total
                            </span>
                        )}
                    </div>
                    {!extracted ? (
                        <div className="border border-dashed border-hair bg-white p-16 text-center">
                            <FileText
                                className="mx-auto text-neutral-300"
                                size={26}
                            />
                            <p className="text-sm text-neutral-500 mt-4">
                                Click <span className="mono">Run Extraction</span>{" "}
                                to have AudRI extract structured compliance
                                rules from the three source documents.
                            </p>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-3">
                            {RULES.map((r, i) => (
                                <RuleCard
                                    key={r.id}
                                    rule={r}
                                    defaultOpen={i === 0}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Right — Rule diff */}
                <aside data-testid="extract-diff">
                    <div className="overline mb-3">Rule diff · Amendment v2.2</div>
                    {!extracted ? (
                        <div className="border border-dashed border-hair bg-white p-8 text-center">
                            <p className="text-[12px] text-neutral-500">
                                Diff appears after extraction completes.
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="border border-hair bg-white p-4 mb-4">
                                <div className="grid grid-cols-3 gap-3 text-center">
                                    <div>
                                        <div className="heading text-2xl font-medium tracking-tighter leading-none text-[#166534]">
                                            {RULE_DIFF.new_count}
                                        </div>
                                        <div className="overline text-[9px] mt-2">
                                            New
                                        </div>
                                    </div>
                                    <div>
                                        <div className="heading text-2xl font-medium tracking-tighter leading-none text-[#9A3412]">
                                            {RULE_DIFF.modified_count}
                                        </div>
                                        <div className="overline text-[9px] mt-2">
                                            Modified
                                        </div>
                                    </div>
                                    <div>
                                        <div className="heading text-2xl font-medium tracking-tighter leading-none text-neutral-500">
                                            {RULE_DIFF.unchanged_count}
                                        </div>
                                        <div className="overline text-[9px] mt-2">
                                            Unchanged
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                {RULE_DIFF.entries.map((e) => (
                                    <DiffCard key={e.id} entry={e} />
                                ))}
                            </div>
                            <div className="mt-4 text-[11px] text-neutral-500 mono leading-relaxed">
                                <Plus
                                    size={11}
                                    className="inline -mt-[2px] mr-1"
                                />
                                Amendment introduces biospecimen storage,
                                biomarker sub-study, and updated venipuncture
                                risks — cascading into 4 new + 2 modified
                                rules.
                            </div>
                        </>
                    )}
                </aside>
            </div>

            {!storyMode && (
                <StepCta
                    to="/icf/execute"
                    label="Continue to Execute"
                    testid="cta-continue-execute"
                />
            )}
            {storyMode && (
                <StoryNarratorBar
                    slide={4}
                    label="1 · Extract"
                    body="AudRI just read the amendment, 21 CFR 50.25 (all 24 elements), and ICH-GCP E6(R3) §4.8. Look at the diff on the right — 4 new rules, 2 modified, 18 unchanged. The two red pain points: biospecimen future use, and the biomarker sub-study procedures. Neither is currently in your ICF."
                    ctaLabel="Show me what breaks"
                    nextTo="/icf/execute?story=1&tab=A"
                />
            )}
        </IcfLayout>
    );
}
